// ✅ src/pages/VehicleV2/DayWiseDailyProgressVehicleWiseReport.jsx
// ✅ FINAL FLOW (NO FILTER AREA)
// - Reads passed state only
// - Auto calls /list API
// - Shows only Back + Print buttons above table
// - Uses localStorage inventory headers
//
// Backend:
//   GET /api/dayWiseDailyProgressVehicleWiseReport/list?vehId=2141&progDate=01/01/2019&progMonth=1&progYear=2019

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaInfoCircle, FaPrint } from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ IMPORTANT: your backend mount
const API_BASE = "https://nissi-india.com/api/dayWiseDailyProgressVehicleWiseReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  if (typeof iso === "string" && iso.includes("/")) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// pick helper for multiple key possibilities
const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== null && v !== undefined && toStr(v) !== "") return v;
  }
  return fallback;
};

export default function DayWiseDailyProgressVehicleWiseReport() {
  const navigate = useNavigate();
  const location = useLocation();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage session values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  // ✅ values from previous page (ONLY PASSING DATA)
  const navState = location?.state || {};
  const passedVehId = toStr(navState?.vehId);
  const passedVehicleCode = toStr(navState?.vehicleCode);

  const passedProgDate = navState?.progDate ? fmtDDMMYYYY(navState?.progDate) : "";
  const passedProgMonth = toStr(navState?.progMonth);
  const passedProgYear = toStr(navState?.progYear);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const autoCalledRef = useRef(false);

  /* ---------------- API: LIST (AUTO) ---------------- */
  const loadList = async () => {
    setLoading(true);
    try {
      if (!passedVehId && !passedVehicleCode) {
        throw new Error("vehId / vehicleCode missing from navigation state");
      }
      if (!toStr(passedProgDate) || !toStr(passedProgMonth) || !toStr(passedProgYear)) {
        throw new Error("progDate / progMonth / progYear missing from navigation state");
      }

      const params = {
        vehId: passedVehId,
        progDate: passedProgDate,
        progMonth: passedProgMonth,
        progYear: passedProgYear,
        vehicleCode: passedVehicleCode,
      };

      const { data } = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      if (!data?.success) throw new Error(data?.message || "List failed");

      const list = Array.isArray(data.rows) ? data.rows : [];
      setRows(list);
      setTotal(Number(data.total || list.length || 0));
    } catch (err) {
      console.error(err);
      setRows([]);
      setTotal(0);
      toast.error(err?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoCalledRef.current) return;
    autoCalledRef.current = true;
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrint = () => {
    navigate("/vehicle/DayWiseDailyProgressReportVehicleWise_Print", {
      state: {
        vehId: passedVehId,
        vehicleCode: passedVehicleCode,
        progDate: passedProgDate,
        progMonth: passedProgMonth,
        progYear: passedProgYear,
      },
    });
  };

  const handleBack = () => navigate(-1);

  const headerLeft = (
    <div className="flex items-center gap-3">
      <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
        Day Wise Daily Progress Vehicle Wise Report
      </h1>

      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
        }`}
      >
        {total} Records
      </span>

      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          isDark ? "bg-gray-700 text-gray-100" : "bg-gray-100 text-gray-700"
        }`}
        title="INFRA SCREEN"
      >
        B-64 (Vehicle Wise)
      </span>
    </div>
  );

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAY WISE DAILY PROGRESS VEHICLE WISE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            {headerLeft}

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleBack}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>

              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-white ${buttonClass} ${hoverClass}`}
              >
                <FaPrint className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>

          <div className={`mb-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            <span className="font-semibold">Vehicle Code:</span> {passedVehicleCode || "-"} &nbsp; | &nbsp;
            <span className="font-semibold">Date:</span> {passedProgDate || "-"} &nbsp; | &nbsp;
            <span className="font-semibold">Month:</span> {passedProgMonth || "-"} &nbsp; | &nbsp;
            <span className="font-semibold">Year:</span> {passedProgYear || "-"}
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {loading ? (
              <div className={`p-10 text-center ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Loading...
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>
                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No data returned for the passed parameters.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">SNo</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Branch</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Progress Date</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Status</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Remarks</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Location</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Total Trips</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Trips Location Wise</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">From</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">To</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Trip.OP HR/KM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Trip.CL HR/KM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Trip.Tot HR/KM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Work Description</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Load</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">UOM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Driver Name</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Starting KM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Closingkm</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Total Working KM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Starting HR</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Closing HR</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Total Working HR</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Load Time</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">UnLoad Time</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Total Time/Minute</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Fuel Tank Opening</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Fuel Issued Qty</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Diesel Amount</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Diesel Consumption</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Production Qty</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Fuel Tank Closing</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Tank Capacity</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">DieselconsumptionKM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">DieselconsumptionHr</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Diesel Cost KM</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Dieselcost HR</th>
                      <th className="px-4 py-3 text-center font-semibold whitespace-nowrap">Avg. Alert</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} hover:${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["SNo", "sno"], idx + 1)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_branchname", "fld_Branchname", "BranchName", "Branch"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {fmtDDMMYYYY(pick(r, ["fld_progressdate", "ProgressDate", "progressdate"]))}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_status", "Status"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_remarks", "Remarks"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_worklocationname", "fld_workLocationname", "Location", "WorkLocation"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_nooftrips", "TotalTrips"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_tripslocationwise", "TripsLocationWise"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_fromLocation", "fld_Fromlocation", "From"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_ToLocation", "fld_Tolocation", "To"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_tripopening", "TripOpening"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_tripclosing", "TripClosing"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_triptotal", "TripTotal"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_descripttion", "fld_Descripttion", "WorkDescription", "Description"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_noofloads", "Load"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_uomname", "fld_UOMname", "UOM"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_drivername", "fld_driverName", "DriverName"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_startingkilometer", "StartingKM"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_closingkilometer", "ClosingKM"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_workingkm", "TotalWorkingKM"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_statrtinghrs", "StartingHR"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_closinghrs", "ClosingHR"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_workinghrs", "TotalWorkingHR"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_loadtime", "LoadTime"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_unloadtime", "UnLoadTime"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_totaltime", "TotalTimeMinute", "TotalTime"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_fueltank_opening", "FuelTankOpening"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_dieselquantity", "FuelIssuedQty"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_dieselamount", "DieselAmount"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_fuelconsumption", "DieselConsumption"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_productionquantity", "ProductionQty"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_fueltank_closing", "FuelTankClosing"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_vehfueltankcapacity", "TankCapacity"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_dieselconsumptionkm", "fld_DieselconsumptionKM", "DieselconsumptionKM"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_dieselconsumptionHrs", "fld_dieselconsumptionhrs", "DieselconsumptionHr"], 0)}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_dieselcostkm", "fld_dieselCostKM", "DieselCostKM"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_dieselcosthr", "fld_dieselcostHR", "DieselcostHR"])}
                        </td>

                        <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {pick(r, ["fld_averagestatus", "AvgAlert", "AverageStatus"])}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}